<template>
    <div class="columns no-bottom-margin is-multiline registered-competitors-top-bar">
        <div class="column is-12">
            <div class="buttons">
                <DropdownButton>
                    <template v-slot:trigger>
                        <button id="notify-competitors-button" class="button is-info" :disabled="!isFullOrganizer">
                            <span class="icon">
                                <i class="fa fa-bell" />
                            </span>
                            <span>{{ translate("predefined_notification.modal.title") }}</span>
                            <span class="icon">
                                <i class="fas fa-angle-down"></i>
                            </span>
                        </button>
                    </template>
                    <template v-slot:content>
                        <a class="dropdown-item" @click="openSendNotificationsModal('paymentReminder')">
                            {{ translate("predefined_notification.type.payment_reminder") }}
                        </a>
                        <a class="dropdown-item" @click="openSendNotificationsModal('singleCompetitorCategories')">
                            {{ translate("predefined_notification.type.single_competitor_categories") }}
                        </a>
                    </template>
                </DropdownButton>

                <a
                    class="button"
                    :class="{ 'is-disabled': !isFullOrganizer }"
                    :href="isFullOrganizer ? eventAdmin().exportPaymentsToCsv(eventId) : '#'"
                >
                    <span class="icon">
                        <i class="fas fa-download"></i>
                    </span>
                    <span>
                        {{ translate("admin_payments.export_csv") }}
                    </span>
                </a>

                <button id="starting-lists-button" class="button" :disabled="!isFullOrganizer" @click="openStartingListsModal">
                    <span class="icon">
                        <i class="fas fa-share-alt"></i>
                    </span>
                    <span>
                        {{ translate("admin_competitors.starting_lists_button") }}
                        <template v-if="startingListsMode">
                            :
                            <span :class="startingListsMode.shared ? 'has-text-success' : 'has-text-danger'">
                                {{ translate(startingListsMode.translationKey) }}
                            </span>
                        </template>
                    </span>
                </button>

                <DropdownButton>
                    <template v-slot:trigger>
                        <button class="button">
                            <span class="icon">
                                <i class="fa fa-filter"></i>
                            </span>
                            <span> {{ translate("common.filter") }}</span>
                            <span class="icon">
                                <i class="fas fa-angle-down"></i>
                            </span>
                        </button>
                    </template>
                    <template v-slot:content>
                        <a class="dropdown-item" @click="toggleMissingPayments">
                            <span v-if="onlyMissingPayments" class="icon">
                                <i class="fa fa-check" />
                            </span>
                            <span>
                                {{ translate("admin_payments.full_filter.missing_payments") }}
                            </span>
                        </a>

                        <a class="dropdown-item" @click="toggleCompletePayments">
                            <span v-if="onlyCompletePayments" class="icon">
                                <i class="fa fa-check" />
                            </span>
                            <span>
                                {{ translate("admin_payments.full_filter.complete_payments") }}
                            </span>
                        </a>

                        <hr class="dropdown-divider" />

                        <a class="dropdown-item" @click="togglePaymentConfirmationFilter('withConfirmation')">
                            <span v-if="paymentConfirmationFilter === 'withConfirmation'" class="icon">
                                <i class="fa fa-check" />
                            </span>
                            <span>
                                {{ translate("admin_payments.full_filter.with_payment_confirmation") }}
                            </span>
                        </a>

                        <a class="dropdown-item" @click="togglePaymentConfirmationFilter('withoutConfirmation')">
                            <span v-if="paymentConfirmationFilter === 'withoutConfirmation'" class="icon">
                                <i class="fa fa-check" />
                            </span>
                            <span>
                                {{ translate("admin_payments.full_filter.without_payment_confirmation") }}
                            </span>
                        </a>

                        <hr class="dropdown-divider" />

                        <a class="dropdown-item" @click="toggleDisqualified">
                            <span v-if="showDisqualified" class="icon">
                                <i class="fa fa-check" />
                            </span>
                            <span>
                                {{ translate("admin_payments.full_filter.disqualified") }}
                            </span>
                        </a>

                        <a class="dropdown-item" @click="toggleDeleted">
                            <span v-if="deleted" class="icon">
                                <i class="fa fa-check" />
                            </span>
                            <span>
                                {{ translate("admin_competitors.show_deleted") }}
                            </span>
                        </a>

                        <hr class="dropdown-divider" />

                        <a class="dropdown-item" @click="clearFilters">
                            <span class="icon">
                                <i class="fa fa-times" />
                            </span>
                            <span>
                                {{ translate("common.clear") }}
                            </span>
                        </a>
                    </template>
                </DropdownButton>

                <button class="button" @click="openPaymentsSettingsModal" :disabled="!isFullOrganizer">
                    <span class="icon">
                        <i class="fa fa-cog"></i>
                    </span>
                    <span>{{ translate("admin_payment.payment_settings") }}</span>
                </button>

                <button class="button" @click="openCompetitorStatsModal" :disabled="!isFullOrganizer">
                    <span class="icon">
                        <i class="fa fa-chart-bar"></i>
                    </span>
                    <span> {{ translate("admin_competitors.stats") }} </span>
                </button>

                <button class="button is-text" v-if="userId > 0" @click="removeSelectedUser">
                    <span class="icon">
                        <i class="fa fa-times" />
                    </span>
                    <span>
                        {{ translate("admin_competitors.remove_selected_user") }}
                    </span>
                </button>
            </div>
        </div>

        <div class="column is-12">
            <div class="is-flex is-align-items-center">
                <QueryInput
                    id="registered-competitors-search"
                    v-model="query"
                    class="no-bottom-margin"
                    @update:modelValue="onSearchQueryChange"
                />

                <RegisteredCompetitorsEnabledFilters />
            </div>
            <p class="help">
                {{ translate("admin_competitors.search_scope_hint") }}
            </p>
        </div>

        <div class="column is-12">
            <BulkSelectButtons @bulkModeEnabled="enableBulkMode" @bulkModeCancelled="cancelBulkMode" @selectionsToggled="togglePage" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted } from "vue";
    import debounce from "lodash.debounce";
    import QueryInput from "../Common/QueryInput.vue";
    import DropdownButton from "../Common/DropdownButton.vue";
    import RegisteredCompetitorsEnabledFilters from "./RegisteredCompetitorsEnabledFilters.vue";
    import {
        eventBus,
        OPEN_COMPETITOR_STATS,
        OPEN_PAYMENT_SETTINGS_MODAL,
        OPEN_STARTING_LISTS_MODAL,
        SEND_NOTIFICATIONS_MODAL,
    } from "./eventBus";
    import history from "../Common/history";
    import urls from "../Urls/urls";
    import { handleError } from "../Common/errorHandler";
    import { translate } from "../Common/translations";
    import BulkSelectButtons from "./BulkSelectButtons.vue";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { PaymentConfirmationFilter } from "../Api/payments";
    import type { PredefinedNotificationType } from "../Api/predefinedNotifications";
    import type { StartingListsSharingType } from "../Api/startingListsApi";
    import { useFeatureAnnouncement } from "../Common/FeatureAnnouncements/useFeatureAnnouncement";
    import type { FeatureAnnouncementKey } from "../Common/FeatureAnnouncements/featureAnnouncementsRegistry";

    const { eventAdmin } = urls;
    const historyInstance = history.getInstance(window);

    const store = useRegisteredCompetitorsStore();

    onMounted(() => {
        if (store.currentUserId > 0) {
            const announcements: Array<{ key: FeatureAnnouncementKey; element: string }> = [
                { key: "registrations_management_category_search", element: "#registered-competitors-search" },
            ];
            if (store.isFullOrganizer) {
                announcements.push(
                    {
                        key: "predefined_notifications_notify_competitors_picker",
                        element: "#notify-competitors-button",
                    },
                    {
                        key: "registered_competitors_starting_lists_button",
                        element: "#starting-lists-button",
                    },
                );
            }
            useFeatureAnnouncement().showEligible(store.currentUserId, announcements);
        }
    });

    const query = computed({
        get: () => store.query,
        set: value => (store.query = value),
    });

    const userId = computed(() => store.userId);
    const onlyMissingPayments = computed(() => store.onlyMissingPayments);
    const onlyCompletePayments = computed(() => store.onlyCompletePayments);
    const showDisqualified = computed(() => store.showDisqualified);
    const deleted = computed(() => store.deleted);
    const paymentConfirmationFilter = computed(() => store.paymentConfirmationFilter);
    const isFullOrganizer = computed(() => store.isFullOrganizer);
    const eventId = computed(() => store.eventId);

    const STARTING_LISTS_MODES: Record<StartingListsSharingType, { translationKey: string; shared: boolean }> = {
        DisableStartingLists: { translationKey: "admin_competitors.starting_lists_hidden", shared: false },
        ShowAllCompetitors: { translationKey: "admin_competitors.starting_lists_mode.show_all", shared: true },
        ShowNotDisqualified: { translationKey: "admin_competitors.starting_lists_mode.show_not_disqualified", shared: true },
        ShowAllAndMarkDisqualified: {
            translationKey: "admin_competitors.starting_lists_mode.show_all_and_mark_disqualified",
            shared: true,
        },
        ShowOnlyVerified: { translationKey: "admin_competitors.starting_lists_mode.show_only_verified", shared: true },
    };

    const startingListsMode = computed(() =>
        store.startingListsSharing ? STARTING_LISTS_MODES[store.startingListsSharing.sharingType] : null,
    );

    const onSearchQueryChange = debounce(() => {
        historyInstance.setQueryParam("q", query.value);
        resetOffset();
        return store.getRegisteredCompetitorsPage().catch(handleError);
    }, 250);

    function resetOffset() {
        historyInstance.setQueryParam("offset", 0);
        store.offset = 0;
    }

    /* BULK SELECT BUTTONS */

    function enableBulkMode() {
        store.bulkModeEnabled = true;
    }
    function cancelBulkMode() {
        store.bulkModeEnabled = false;
        store.selectedCompetitors = {};
    }
    function togglePage(selected: boolean) {
        store.togglePage(selected);
    }

    /* MODALS */

    function openSendNotificationsModal(type: PredefinedNotificationType) {
        eventBus.$emit(SEND_NOTIFICATIONS_MODAL, type);
    }

    function openPaymentsSettingsModal() {
        eventBus.$emit(OPEN_PAYMENT_SETTINGS_MODAL);
    }

    function openStartingListsModal() {
        eventBus.$emit(OPEN_STARTING_LISTS_MODAL);
    }

    function openCompetitorStatsModal() {
        eventBus.$emit(OPEN_COMPETITOR_STATS);
    }

    /* MANIPULATING FILTERS */

    function toggleDeleted() {
        const newValue = !store.deleted;
        store.deleted = newValue;
        historyInstance.setQueryParam("deleted", newValue ? "1" : null);
        resetOffset();

        if (store.deleted) {
            store.showDisqualified = false;
            //Deleted competitors CANNOT be managed via bulk mode updates.
            store.cancelBulkMode();
            historyInstance.setQueryParam("disqualified", null);
        }

        return store.getRegisteredCompetitorsPage().catch(handleError);
    }

    function removeSelectedUser() {
        store.userId = 0;
        historyInstance.setQueryParam("userId", store.userId);
        store.offset = 0;
        return store.getRegisteredCompetitorsPage().catch(handleError);
    }

    function toggleMissingPayments() {
        const newValue = !store.onlyMissingPayments;
        store.onlyMissingPayments = newValue;
        historyInstance.setQueryParam("onlyMissingPayments", newValue ? "1" : null);

        if (newValue) {
            store.onlyCompletePayments = false;
            historyInstance.setQueryParam("onlyCompletePayments", null);
        }

        resetOffset();
        return store.getRegisteredCompetitorsPage().catch(handleError);
    }

    function toggleCompletePayments() {
        const newValue = !store.onlyCompletePayments;
        store.onlyCompletePayments = newValue;
        historyInstance.setQueryParam("onlyCompletePayments", newValue ? "1" : null);

        if (newValue) {
            store.onlyMissingPayments = false;
            historyInstance.setQueryParam("onlyMissingPayments", null);
        }
        resetOffset();
        return store.getRegisteredCompetitorsPage().catch(handleError);
    }

    function toggleDisqualified() {
        store.showDisqualified = !store.showDisqualified;
        resetOffset();

        if (store.showDisqualified) {
            historyInstance.setQueryParam("disqualified", store.showDisqualified ? "1" : "0");
            store.deleted = false;
            historyInstance.setQueryParam("deleted", null);
        }

        return store.getRegisteredCompetitorsPage().catch(handleError);
    }

    function togglePaymentConfirmationFilter(filter: PaymentConfirmationFilter) {
        const currentValue = store.paymentConfirmationFilter;

        const newValue: PaymentConfirmationFilter = currentValue === filter ? "all" : filter;

        store.paymentConfirmationFilter = newValue;
        historyInstance.setQueryParam("paymentConfirmationFilter", newValue === "all" ? null : newValue);

        resetOffset();
        return store.getRegisteredCompetitorsPage().catch(handleError);
    }

    function clearFilters() {
        resetOffset();
        store.resetFilters();

        return store.getRegisteredCompetitorsPage().catch(handleError);
    }
</script>

<style scoped></style>
